const responseCache = new Map();
const pendingRequests = new Map();
const MAX_CACHE_ENTRIES = 100;

export const HOMEPAGE_CACHE_TTL = {
  live: 15 * 1000,
  fast: 60 * 1000,
  categories: 5 * 60 * 1000,
  sellers: 5 * 60 * 1000,
};

const abortError = () => {
  const error = new Error('The request was aborted');
  error.name = 'AbortError';
  return error;
};

const withCallerAbort = (promise, signal) => {
  if (!signal) return promise;
  if (signal.aborted) return Promise.reject(abortError());

  return new Promise((resolve, reject) => {
    const handleAbort = () => reject(abortError());
    signal.addEventListener('abort', handleAbort, { once: true });
    promise.then(
      (value) => {
        signal.removeEventListener('abort', handleAbort);
        resolve(value);
      },
      (error) => {
        signal.removeEventListener('abort', handleAbort);
        reject(error);
      }
    );
  });
};

const pruneCache = (now) => {
  responseCache.forEach((entry, key) => {
    if (entry.expiresAt <= now) responseCache.delete(key);
  });
  while (responseCache.size >= MAX_CACHE_ENTRIES) {
    responseCache.delete(responseCache.keys().next().value);
  }
};

export const appendLocationPreference = (endpoint, location) => {
  const country = String(location?.country || '').trim();
  const region = String(location?.region || '').trim();
  const city = String(location?.city || '').trim();
  if (country && country !== 'Unknown') endpoint.searchParams.set('user_country', country);
  if (region) endpoint.searchParams.set('user_region', region);
  if (city) endpoint.searchParams.set('user_city', city);
  return endpoint;
};

export const homepageAuthHeaders = (user) => {
  const token = user ? localStorage.getItem('token') : null;
  return token ? { Authorization: `Bearer ${token}` } : {};
};

export const homepageViewerScope = (user) => {
  const userId = user?.id || user?.userid || user?.idusers || user?.email;
  return userId ? `user:${userId}` : 'guest';
};

// Keeps homepage GETs in memory for a short time and shares one network request
// among simultaneous consumers. A component abort only unsubscribes that caller;
// it does not cancel the shared request needed by another mounted component.
export const fetchHomepageJson = (url, {
  headers = {},
  signal,
  scope = 'guest',
  variant = '',
  ttlMs = HOMEPAGE_CACHE_TTL.fast,
} = {}) => {
  const key = `${scope}|${variant}|${String(url)}`;
  const now = Date.now();
  const cached = responseCache.get(key);
  if (cached && cached.expiresAt > now) return withCallerAbort(Promise.resolve(cached.data), signal);
  if (cached) responseCache.delete(key);

  let request = pendingRequests.get(key);
  if (!request) {
    request = fetch(url, { headers })
      .then(async (response) => {
        const data = await response.json();
        if (!response.ok) {
          const error = new Error(data?.error || `Request failed with status ${response.status}`);
          error.status = response.status;
          throw error;
        }
        pruneCache(Date.now());
        responseCache.set(key, { data, expiresAt: Date.now() + ttlMs });
        return data;
      })
      .finally(() => pendingRequests.delete(key));
    pendingRequests.set(key, request);
  }

  return withCallerAbort(request, signal);
};

export const clearHomepageCache = () => responseCache.clear();
